import { Fragment, useCallback, useEffect } from "react";
import BaseDrawer from "@/shared/components/Drawer";
import type { Supplier } from "@/shared/types/supplier.types";
import { SupplierFiltersProvider } from "../../contexts/SupplierFiltersContext";
import SupplierDrawer from "../SupplierDrawer";
import SuppliersSelectionDrawerContent from "./SuppliersSelectionDrawerContent";
import { useSuppliersDrawerActions } from "./useSuppliersDrawerActions";

type SuppliersSelectionDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSupplierSelected?: (supplier: Supplier) => void
  onCloseAll?: () => void
  title?: string
  description?: string
  width?: string
  zIndex?: number
};

const DEFAULT_TITLE = "Entreprises";
const DEFAULT_DESCRIPTION = "Gérez vos entreprises et leurs informations.";

const SuppliersSelectionDrawer = ({
  open,
  onOpenChange,
  onSupplierSelected,
  onCloseAll,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  width = "550px",
  zIndex,
}: SuppliersSelectionDrawerProps) => {
  const {
    resetAll,
    openEditForm,
    deleteSupplier,
    openCreateForm,
    formDrawerOpen,
    closeFormDrawer,
    supplierToEdit,
  } = useSuppliersDrawerActions();

  useEffect(() => {
    if (!open) resetAll();
  }, [open, resetAll]);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      onCloseAll ? onCloseAll() : onOpenChange(false);
    }
    else {
      onOpenChange(isOpen);
    }
  }, [onCloseAll, onOpenChange]);

  const handleSupplierSelected = useCallback((supplier: Supplier) => {
    if (onSupplierSelected) {
      onSupplierSelected(supplier);
      onOpenChange(false);
    }
    else {
      openEditForm(supplier);
    }
  }, [onSupplierSelected, onOpenChange, openEditForm]);

  return (
    <Fragment>
      <BaseDrawer
        open={open}
        onOpenChange={handleOpenChange}
        title={title}
        description={description}
        isFirstLevel={false}
        onBack={handleClose}
        width={width}
        zIndex={zIndex}
      >
        <SupplierFiltersProvider>
          <SuppliersSelectionDrawerContent
            onSupplierSelected={handleSupplierSelected}
            onEditSupplier={openEditForm}
            onDeleteSupplier={deleteSupplier}
            onCreateSupplier={openCreateForm}
          />
        </SupplierFiltersProvider>
      </BaseDrawer>

      <SupplierDrawer
        open={formDrawerOpen}
        onOpenChange={closeFormDrawer}
        data={supplierToEdit}
        width={width}
        zIndex={zIndex ? zIndex + 5 : undefined}
      />
    </Fragment>
  );
};

export default SuppliersSelectionDrawer;
